"use client"

import { useEffect, type ReactNode } from "react"
import { ChevronLeftIcon, CloseIcon } from "@/components/icons/pixel-icons"
import { usePlayerPrefs } from "@/lib/player-prefs"

/**
 * Responsive chrome for the main menu.
 *
 * Desktop (>= 1024px): the grouped entry list stays visible on the left and the
 * selected entry renders in a detail pane on the right, so switching sections
 * never hides the navigation.
 *
 * Mobile / narrow: a drill-down. Level one is the entry list; choosing an entry
 * slides the detail over it with a back button, which is what a phone user
 * expects and avoids cramming two panes into 360px.
 *
 * The sheet itself matches the codex bottom sheet so both surfaces read as one
 * UI.
 */


interface MenuShellProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** Grouped entry list (level one) */
  navContent: ReactNode
  /** Detail for the selected entry, or null when nothing is selected */
  detailContent: ReactNode
  /** Heading shown above the detail on mobile, next to the back button */
  detailTitle?: string
  onBack: () => void
  isNarrow: boolean
}

export const MenuShell = ({
  isOpen,
  onClose,
  title,
  navContent,
  detailContent,
  detailTitle,
  onBack,
  isNarrow,
}: MenuShellProps) => {
  const { reducedMotion } = usePlayerPrefs()

  // Lock body scroll while the sheet owns the screen
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  // Escape closes the sheet, or steps back one level when drilled in
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.stopPropagation()
      if (isNarrow && detailContent) onBack()
      else onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isOpen, isNarrow, detailContent, onBack, onClose])

  if (!isOpen) return null

  const showMobileDetail = isNarrow && detailContent !== null

  return (
    <>
      <div
        id="game-menu-backdrop"
        className={`fixed inset-0 bg-black/70 z-[80] backdrop-blur-sm ${
          reducedMotion ? "" : "animate-in fade-in duration-200"
        }`}
        onClick={onClose}
      />

      <div
        id="game-menu-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed left-0 right-0 bottom-0 z-[90] bg-gradient-to-b from-slate-900 to-slate-950 border-t-2 border-amber-600/50 shadow-2xl shadow-black/50 overflow-hidden rounded-t-2xl flex flex-col ${
          isNarrow ? "h-[88vh]" : "h-[80vh] mx-auto max-w-[1400px]"
        } ${reducedMotion ? "" : "animate-in slide-in-from-bottom duration-300"}`}
      >
        {/* Header: title, or back + detail title once drilled in on mobile */}
        <div className="relative flex items-center gap-2 px-4 pt-4 pb-3 border-b border-amber-600/30">
          {showMobileDetail ? (
            <>
              <button
                id="game-menu-back-button"
                onClick={onBack}
                className="flex items-center gap-1 -ml-1 px-2 py-1.5 rounded-lg text-amber-300 hover:bg-white/5 transition"
                aria-label="Back to menu"
              >
                <ChevronLeftIcon size={18} />
                <span className="text-sm font-bold">Menu</span>
              </button>
              <h2 className="flex-1 text-center text-lg font-bold text-amber-300 truncate pr-10">
                {detailTitle}
              </h2>
            </>
          ) : (
            <h2 className="flex-1 text-center text-2xl font-bold text-amber-300">{title}</h2>
          )}

          <button
            id="game-menu-close-button"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 text-white rounded-lg transition border border-gray-700 hover:border-gray-500"
            title="Close"
            aria-label="Close menu"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        {isNarrow ? (
          // Drill-down: one pane at a time
          <div className="flex-1 overflow-hidden">
            {showMobileDetail ? (
              <div
                key={detailTitle}
                className={`h-full overflow-y-auto px-4 py-4 ${
                  reducedMotion ? "" : "animate-in slide-in-from-right duration-200"
                }`}
                style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
              >
                {detailContent}
              </div>
            ) : (
              <div
                className="h-full overflow-y-auto px-4 py-4"
                style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
              >
                {navContent}
              </div>
            )}
          </div>
        ) : (
          // Two panes: persistent nav + detail
          <div className="flex-1 flex overflow-hidden">
            <div
              className="w-[360px] shrink-0 overflow-y-auto px-4 py-4 border-r border-amber-600/30"
              style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
            >
              {navContent}
            </div>
            <div
              className="flex-1 overflow-y-auto px-6 py-5 bg-black/20"
              style={{ scrollbarWidth: "thin", scrollbarColor: "#92400e #1e293b" }}
            >
              {detailContent ?? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500 text-sm">Pick something on the left</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
